/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  CommunicationSettingsProfile,
  CommunicationSettingsProfileReferent,
  CreateCommunicationSettingsProfileInput,
  CreateCommunicationSettingsProfileMutationVariables,
  DateTime,
  DeleteCommunicationSettingsProfileMutationVariables,
  LooseObject,
  OnCreateCommunicationSettingsProfileSubscription,
  OnDeleteCommunicationSettingsProfileSubscription,
  OnUpdateCommunicationSettingsProfileSubscription,
  UpdateCommunicationSettingsProfileInput,
  cleanInputCreate,
  cleanInputUpdate,
  createCommunicationSettingsProfile as createMutation,
  deleteCommunicationSettingsProfile as deleteMutation,
  getDateTimeDifference,
  getCommunicationSettingsProfile as getQuery,
  getReadId,
  getTableClientId,
  onCreateCommunicationSettingsProfile as onCreateSubscription,
  onDeleteCommunicationSettingsProfile as onDeleteSubscription,
  onUpdateCommunicationSettingsProfile as onUpdateSubscription,
  syncCommunicationSettingsProfiles,
  todayDateTime,
  updateCommunicationSettingsProfile as updateMutation,
} from '@rentguru/commons-utils';
import {
  NUMBER_OF_MINUTES_FOR_REFETCH,
  deleteEntityWithFetchBefore,
  get,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { Reducer, useContext, useEffect, useMemo, useReducer } from 'react';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export interface CommunicationSettingsProfileContext extends State {
  getDefaultLeaseCommunicationSettingsProfiles: () => CommunicationSettingsProfile[];
  getLeaseCommunicationSettingsProfiles: () => CommunicationSettingsProfile[];
  getDefaultOwnerCommunicationSettingsProfiles: () => CommunicationSettingsProfile[];
  getOwnerCommunicationSettingsProfile: (
    communicationSettingProfileId: string
  ) => CommunicationSettingsProfile | undefined;
  getTechnicCommunicationSettingsProfile: (
    communicationSettingProfileId: string
  ) => CommunicationSettingsProfile | undefined;
  getDefaultTechnicCommunicationSettingsProfiles: () => CommunicationSettingsProfile[];
  createCommunicationSettingsProfile: (
    input:
      | CreateCommunicationSettingsProfileInput
      | Omit<CreateCommunicationSettingsProfileInput, 'clientId' | 'readId'>
  ) => Promise<CommunicationSettingsProfile>;
  updateCommunicationSettingsProfile: (
    original: CommunicationSettingsProfile,
    updates: Partial<UpdateCommunicationSettingsProfileInput>
  ) => Promise<CommunicationSettingsProfile>;
  deleteCommunicationSettingsProfile: (
    communicationSettingsProfile: CommunicationSettingsProfile,
    replacementId?: string
  ) => Promise<CommunicationSettingsProfile | null>;
}

interface State {
  loading: boolean;
  error: string | null;
  shouldFetch: boolean;
  lastCommunicationSettingsProfilesFetch: DateTime | null;
  defaultCommunicationSettingsProfiles: CommunicationSettingsProfile[] | null;
  communicationSettingsProfiles: CommunicationSettingsProfile[] | null;
}

type Action =
  | {
      type: 'FETCH_COMMUNICATION_SETTINGS_PROFILES';
      payload?: { force: boolean };
    }
  | {
      type: 'FETCHED';
      payload: {
        defaultCommunicationSettingsProfiles: CommunicationSettingsProfile[];
        communicationSettingsProfiles: CommunicationSettingsProfile[];
      };
    }
  | {
      type: 'FETCH_FAILED';
      payload: { error: string };
    }
  | {
      type: 'ADD' | 'UPDATE';
      payload: { communicationSettingsProfile: CommunicationSettingsProfile };
    }
  | {
      type: 'DELETE';
      payload: { id: string };
    };

const initialState: State = {
  loading: false,
  error: null,
  shouldFetch: true,
  defaultCommunicationSettingsProfiles: null,
  communicationSettingsProfiles: null,
  lastCommunicationSettingsProfilesFetch: null,
};

const contextReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_COMMUNICATION_SETTINGS_PROFILES':
      // Check lastFetch
      if (
        !action.payload?.force &&
        (state.loading ||
          state.shouldFetch ||
          (state.lastCommunicationSettingsProfilesFetch &&
            getDateTimeDifference(
              { start: state.lastCommunicationSettingsProfilesFetch, end: todayDateTime() },
              'minutes'
            ) < NUMBER_OF_MINUTES_FOR_REFETCH))
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
        loading: true,
      };
    case 'FETCHED':
      // Build List with merged object
      const newDefaultCommunicationSettingsProfiles = [
        ...action.payload.defaultCommunicationSettingsProfiles,
        ...(state.defaultCommunicationSettingsProfiles ?? []).filter(
          (oldCommunicationSettingsProfile) =>
            !action.payload.defaultCommunicationSettingsProfiles.some(
              (newCommunicationSettingsProfile) =>
                newCommunicationSettingsProfile.id === oldCommunicationSettingsProfile.id
            )
        ),
      ];

      const newCommunicationSettingsProfilesMap = new Map();
      (state.communicationSettingsProfiles ?? []).forEach((profile) => {
        newCommunicationSettingsProfilesMap.set(profile.id, profile);
      });
      action.payload.communicationSettingsProfiles.forEach((profile) => {
        newCommunicationSettingsProfilesMap.set(profile.id, profile);
      });
      const newCommunicationSettingsProfiles = Array.from(newCommunicationSettingsProfilesMap.values());

      return {
        ...state,
        loading: !isEmpty(state.shouldFetch),
        shouldFetch: false,
        lastCommunicationSettingsProfilesFetch: todayDateTime(),
        defaultCommunicationSettingsProfiles: newDefaultCommunicationSettingsProfiles,
        communicationSettingsProfiles: newCommunicationSettingsProfiles,
        error: null,
      };
    case 'FETCH_FAILED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        error: action.payload.error,
      };
    case 'ADD':
      if (!state.defaultCommunicationSettingsProfiles) {
        return state;
      }
      const entity = action.payload.communicationSettingsProfile;
      if (!entity.isDefaultSetting) {
        return state;
      }
      return {
        ...state,
        defaultCommunicationSettingsProfiles: [...state.defaultCommunicationSettingsProfiles, entity],
      };
    case 'UPDATE':
      if (!state.defaultCommunicationSettingsProfiles) {
        return state;
      }

      return {
        ...state,
        defaultCommunicationSettingsProfiles: state.defaultCommunicationSettingsProfiles.map((item) => {
          if (item.id === action.payload.communicationSettingsProfile.id) {
            return action.payload.communicationSettingsProfile;
          }
          return item;
        }),
      };
    case 'DELETE':
      if (!state.defaultCommunicationSettingsProfiles) {
        return state;
      }

      return {
        ...state,
        defaultCommunicationSettingsProfiles: state.defaultCommunicationSettingsProfiles.filter(
          (item) => item.id !== action.payload.id
        ),
      };
    default:
      return state;
  }
};

export const getCommunicationSettingsProfile = async (id: string) =>
  await get<CommunicationSettingsProfile>(getQuery, id);

const fetchCommunicationSettingsProfiles = async (clientId: string, additionalFilter?: LooseObject) =>
  await list<CommunicationSettingsProfile>(syncCommunicationSettingsProfiles, 'clientId', clientId, additionalFilter);

export const CommunicationSettingsProfileContext = React.createContext<CommunicationSettingsProfileContext | null>(
  null
);

export const CommunicationSettingsProfileContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { settingsAutomationWrite } = usePermissions();
  const { clientId, isTenant } = useUser();
  const hasNoAccessToCommunicationSettingsProfiles = isTenant;
  const [state, dispatch] = useReducer<Reducer<State, Action>>(contextReducer, initialState);

  useSubscriptions<
    OnCreateCommunicationSettingsProfileSubscription,
    OnUpdateCommunicationSettingsProfileSubscription,
    OnDeleteCommunicationSettingsProfileSubscription
  >(
    onCreateSubscription,
    onUpdateSubscription,
    onDeleteSubscription,
    (data) => {
      if (!data?.onCreateCommunicationSettingsProfile) {
        return;
      }
      const communicationSettingsProfile: CommunicationSettingsProfile = data.onCreateCommunicationSettingsProfile;
      dispatch({ type: 'ADD', payload: { communicationSettingsProfile } });
    },
    (data) => {
      if (!data?.onUpdateCommunicationSettingsProfile) {
        return;
      }
      const communicationSettingsProfile: CommunicationSettingsProfile = data.onUpdateCommunicationSettingsProfile;
      dispatch({ type: 'UPDATE', payload: { communicationSettingsProfile } });
    },
    (data) => {
      if (!data?.onDeleteCommunicationSettingsProfile) {
        return;
      }
      const communicationSettingsProfile: CommunicationSettingsProfile = data.onDeleteCommunicationSettingsProfile;
      dispatch({ type: 'DELETE', payload: { id: communicationSettingsProfile.id } });
    }
  );

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      if (hasNoAccessToCommunicationSettingsProfiles) {
        if (!unmounted) {
          dispatch({
            type: 'FETCHED',
            payload: { defaultCommunicationSettingsProfiles: [], communicationSettingsProfiles: [] },
          });
        }
        return;
      }
      try {
        const communicationSettingsProfiles = await fetchCommunicationSettingsProfiles(
          getTableClientId(clientId!, 'CommunicationSettingsProfile')
        );

        const defaultCommunicationSettingsProfiles = communicationSettingsProfiles.filter(
          (communicationSettingsProfile) => communicationSettingsProfile.isDefaultSetting
        );

        let technicProfileIsMissing = true;
        let leaseProfileIsMissing = true;
        let ownerProfileIsMissing = true;
        for (const profile of defaultCommunicationSettingsProfiles) {
          if (ownerProfileIsMissing && profile.referent === CommunicationSettingsProfileReferent.OWNER) {
            ownerProfileIsMissing = false;
          } else if (leaseProfileIsMissing && profile.referent === CommunicationSettingsProfileReferent.LEASE) {
            leaseProfileIsMissing = false;
          } else if (technicProfileIsMissing && profile.referent === CommunicationSettingsProfileReferent.TECHNIC) {
            technicProfileIsMissing = false;
          }
        }

        if (ownerProfileIsMissing || leaseProfileIsMissing || technicProfileIsMissing) {
          throw new Error('A communication settings profile is missing');
        }
        if (!unmounted) {
          dispatch({
            type: 'FETCHED',
            payload: { defaultCommunicationSettingsProfiles, communicationSettingsProfiles },
          });
        }
      } catch (error) {
        console.error('[communicationSettingsProfiles] fetching communication settings profiles failed', error);
        if (!unmounted) {
          dispatch({
            type: 'FETCH_FAILED',
            payload: { error: error instanceof Error ? error.message : String(error) },
          });
        }
      }
    };
    if (state.shouldFetch) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch, hasNoAccessToCommunicationSettingsProfiles]);

  const getDefaultLeaseCommunicationSettingsProfiles = () => {
    if (state.loading || state.error || !state.defaultCommunicationSettingsProfiles) {
      return [];
    }

    return state.defaultCommunicationSettingsProfiles.filter(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.LEASE
    );
  };

  const getLeaseCommunicationSettingsProfiles = () => {
    if (state.loading || state.error || !state.communicationSettingsProfiles) {
      return [];
    }

    return state.communicationSettingsProfiles.filter(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.LEASE
    );
  };

  const getOwnerCommunicationSettingsProfile = (communicationSettingProfileId: string) => {
    if (state.loading || state.error || !state.communicationSettingsProfiles) {
      return undefined;
    }

    const settings = state.communicationSettingsProfiles.find(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.OWNER &&
        communicationSettingProfile.id === communicationSettingProfileId
    );
    return settings;
  };

  const getTechnicCommunicationSettingsProfile = (communicationSettingProfileId: string) => {
    if (state.loading || state.error || !state.communicationSettingsProfiles) {
      return undefined;
    }

    return state.communicationSettingsProfiles.find(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.TECHNIC &&
        communicationSettingProfile.id === communicationSettingProfileId
    );
  };

  const getDefaultTechnicCommunicationSettingsProfiles = () => {
    if (state.loading || state.error || !state.defaultCommunicationSettingsProfiles) {
      return [];
    }

    return state.defaultCommunicationSettingsProfiles.filter(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.TECHNIC
    );
  };

  const getDefaultOwnerCommunicationSettingsProfiles = () => {
    if (state.loading || state.error || !state.defaultCommunicationSettingsProfiles) {
      return [];
    }

    return state.defaultCommunicationSettingsProfiles.filter(
      (communicationSettingProfile) =>
        communicationSettingProfile.referent === CommunicationSettingsProfileReferent.OWNER
    );
  };

  const createCommunicationSettingsProfile = async (
    input:
      | CreateCommunicationSettingsProfileInput
      | Omit<CreateCommunicationSettingsProfileInput, 'clientId' | 'readId'>
  ): Promise<CommunicationSettingsProfile> => {
    const communicationSettingsProfile = await mutation<
      CommunicationSettingsProfile,
      CreateCommunicationSettingsProfileMutationVariables
    >(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateCommunicationSettingsProfileInput),
        clientId: getTableClientId(clientId!, 'CommunicationSettingsProfile'),
        readId: getReadId(clientId!, 'CommunicationSettingsProfile'),
      },
    });
    return communicationSettingsProfile;
  };

  const updateCommunicationSettingsProfile = async (
    original: CommunicationSettingsProfile,
    updates: Partial<UpdateCommunicationSettingsProfileInput>
  ): Promise<CommunicationSettingsProfile> => {
    const communicationSettingsProfile = await mutation<CommunicationSettingsProfile>(updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    return communicationSettingsProfile;
  };

  const deleteCommunicationSettingsProfile = async (
    communicationSettingsProfile: CommunicationSettingsProfile,
    replacementId?: string
  ): Promise<CommunicationSettingsProfile | null> => {
    if (!settingsAutomationWrite) {
      return null;
    }
    const profileToDelete = isNil(replacementId)
      ? communicationSettingsProfile
      : await mutation<CommunicationSettingsProfile>(updateMutation, {
          input: {
            ...cleanInputUpdate(
              { id: communicationSettingsProfile.id, _version: communicationSettingsProfile._version, replacementId },
              false
            ),
          },
        });

    const result = await deleteEntityWithFetchBefore<
      CommunicationSettingsProfile,
      DeleteCommunicationSettingsProfileMutationVariables
    >(profileToDelete, getQuery, deleteMutation);
    return result;
  };

  const values = useMemo(
    () => ({
      ...state,
      getDefaultLeaseCommunicationSettingsProfiles,
      getLeaseCommunicationSettingsProfiles,
      getDefaultTechnicCommunicationSettingsProfiles,
      getDefaultOwnerCommunicationSettingsProfiles,
      getOwnerCommunicationSettingsProfile,
      getTechnicCommunicationSettingsProfile,
      createCommunicationSettingsProfile,
      updateCommunicationSettingsProfile,
      deleteCommunicationSettingsProfile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return (
    <CommunicationSettingsProfileContext.Provider value={values}>
      {children}
    </CommunicationSettingsProfileContext.Provider>
  );
};

export const useCommunicationSettingsProfiles = (): CommunicationSettingsProfileContext => {
  const context = useContext<CommunicationSettingsProfileContext | null>(CommunicationSettingsProfileContext);

  if (!context) {
    throw new Error(
      '`useCommunicationSettingsProfiles` hook must be used within a `CommunicationSettingsProfileContext` component'
    );
  }

  return context;
};
